// frozen_string_literal: true

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    color: { type: String, default: "#deaf13" }, // Default to a green color
    duration: { type: Number, default: 200 }, // Duration in milliseconds
    intensity: { type: Number, default: 0.3 }, // Effect intensity
    pulseWidth: { type: Number, default: 0.1 }, // Width of the leading edge (0-1)
    pulseSpeed: { type: Number, default: 1.5 }, // Speed multiplier for the leading edge
  };

  connect() {
    // Initialize animation state
    this.isAnimating = false;
    this.startTime = null;
    this.animationFrameId = null;
    this.mouseX = null;
    this.mouseY = null;

    // Get the accent color from CSS variables if available
    this.initializeAccentColor();

    this.setupCanvas();
    this.setupWebGL();

    // Only proceed if WebGL is supported
    if (this.gl) {
      this.setupShaders();
      this.setupBuffers();

      // Add a bound reference to the event handler
      this.boundHandleTaskCompleted = this.handleTaskCompleted.bind(this);

      // Listen for task completion events
      document.addEventListener(
        "task:completed",
        this.boundHandleTaskCompleted,
      );

      // Setup keyboard shortcut for debug panel
      this.setupDebugPanelShortcut();
    }
  }

  disconnect() {
    // Remove event listener
    if (this.boundHandleTaskCompleted) {
      document.removeEventListener(
        "task:completed",
        this.boundHandleTaskCompleted,
      );
    }

    // Clean up WebGL resources
    if (this.gl) {
      this.gl.getExtension("WEBGL_lose_context")?.loseContext();
    }

    // Remove debug elements
    const debugBtn = document.getElementById("effect-debug-toggle");
    if (debugBtn) debugBtn.remove();

    const debugPanel = document.getElementById("effect-debug-panel");
    if (debugPanel) debugPanel.remove();

    // Remove canvas
    if (this.canvas) {
      this.canvas.remove();
    }

    // Remove keyboard event listener
    document.removeEventListener("keydown", this.handleKeyDown);
  }

  setupCanvas() {
    // Create a fullscreen canvas that sits behind all content
    this.canvas = document.createElement("canvas");
    this.canvas.id = "task-completion-effect";
    this.canvas.style.position = "fixed";
    this.canvas.style.top = "0";
    this.canvas.style.left = "0";
    this.canvas.style.width = "100vw";
    this.canvas.style.height = "100vh";
    this.canvas.style.pointerEvents = "none"; // Don't capture mouse events
    this.canvas.style.zIndex = "5"; // Above background but below most UI elements

    // Add canvas to the DOM
    document.body.prepend(this.canvas);

    // Set canvas size to match window
    this.resizeCanvas();
    window.addEventListener("resize", this.resizeCanvas.bind(this));
  }

  resizeCanvas() {
    if (!this.canvas) return;

    const pixelRatio = window.devicePixelRatio || 1;
    this.canvas.width = window.innerWidth * pixelRatio;
    this.canvas.height = window.innerHeight * pixelRatio;

    // Update WebGL viewport if initialized
    if (this.gl) {
      this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    }
  }

  setupWebGL() {
    // Initialize WebGL context
    this.gl =
      this.canvas.getContext("webgl") ||
      this.canvas.getContext("experimental-webgl");

    if (!this.gl) {
      return;
    }

    // Set clear color to transparent
    this.gl.clearColor(0.0, 0.0, 0.0, 0.0);
    this.gl.enable(this.gl.BLEND);
    this.gl.blendFunc(this.gl.SRC_ALPHA, this.gl.ONE_MINUS_SRC_ALPHA);

    // Set viewport
    this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
  }

  setupShaders() {
    // Vertex shader program
    const vsSource = `
      attribute vec4 aVertexPosition;
      attribute vec2 aTextureCoord;

      varying highp vec2 vTextureCoord;

      void main(void) {
        gl_Position = aVertexPosition;
        vTextureCoord = aTextureCoord;
      }
    `;

    // Fragment shader program
    const fsSource = `
      precision mediump float;

      varying highp vec2 vTextureCoord;

      uniform vec2 uResolution;
      uniform vec2 uMousePosition;
      uniform float uTime;
      uniform vec3 uColor;
      uniform float uIntensity;
      uniform float uPulseWidth;
      uniform float uPulseSpeed;

      void main(void) {
        // Calculate the pulse position (0 to 1)
        float pulsePosition = uTime * uPulseSpeed;

        // Calculate distance from current pixel to pulse position
        float distanceFromPulse = abs(vTextureCoord.x - pulsePosition);

        // Create a smooth pulse shape
        float pulse = smoothstep(uPulseWidth, 0.0, distanceFromPulse);

        // Calculate fade out based on when the pulse reaches the left side
        float fadeOutStart = 0.9;
        float fadeOut = 1.0;

        if (pulsePosition >= fadeOutStart) {
          fadeOut = smoothstep(1.0, fadeOutStart, pulsePosition);
        }

        // Calculate final alpha - only show the pulse
        float alpha = pulse * fadeOut * uIntensity;

        // Set the final color with the calculated alpha
        gl_FragColor = vec4(uColor, alpha);
      }
    `;

    // Initialize shader program
    const vertexShader = this.loadShader(this.gl.VERTEX_SHADER, vsSource);
    const fragmentShader = this.loadShader(this.gl.FRAGMENT_SHADER, fsSource);

    if (!vertexShader || !fragmentShader) {
      return;
    }

    // Create the shader program
    this.program = this.gl.createProgram();
    this.gl.attachShader(this.program, vertexShader);
    this.gl.attachShader(this.program, fragmentShader);
    this.gl.linkProgram(this.program);

    // Check if shader program linked successfully
    if (!this.gl.getProgramParameter(this.program, this.gl.LINK_STATUS)) {
      return;
    }

    // Get attribute and uniform locations
    this.programInfo = {
      program: this.program,
      attribLocations: {
        vertexPosition: this.gl.getAttribLocation(
          this.program,
          "aVertexPosition",
        ),
        textureCoord: this.gl.getAttribLocation(this.program, "aTextureCoord"),
      },
      uniformLocations: {
        resolution: this.gl.getUniformLocation(this.program, "uResolution"),
        mousePosition: this.gl.getUniformLocation(
          this.program,
          "uMousePosition",
        ),
        time: this.gl.getUniformLocation(this.program, "uTime"),
        color: this.gl.getUniformLocation(this.program, "uColor"),
        intensity: this.gl.getUniformLocation(this.program, "uIntensity"),
        pulseWidth: this.gl.getUniformLocation(this.program, "uPulseWidth"),
        pulseSpeed: this.gl.getUniformLocation(this.program, "uPulseSpeed"),
      },
    };

    // Use the program once to initialize
    this.gl.useProgram(this.program);

    // Set initial resolution uniform
    this.gl.uniform2f(
      this.programInfo.uniformLocations.resolution,
      this.canvas.width,
      this.canvas.height,
    );
  }

  loadShader(type, source) {
    const shader = this.gl.createShader(type);
    this.gl.shaderSource(shader, source);
    this.gl.compileShader(shader);

    // Check if compilation was successful
    if (!this.gl.getShaderParameter(shader, this.gl.COMPILE_STATUS)) {
      this.gl.deleteShader(shader);
      return null;
    }

    return shader;
  }

  setupBuffers() {
    // Create a buffer for the square's positions
    this.positionBuffer = this.gl.createBuffer();
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.positionBuffer);

    // Create a square that covers the entire canvas
    const positions = [
      -1.0,
      -1.0, // bottom left
      1.0,
      -1.0, // bottom right
      -1.0,
      1.0, // top left
      1.0,
      1.0, // top right
    ];

    this.gl.bufferData(
      this.gl.ARRAY_BUFFER,
      new Float32Array(positions),
      this.gl.STATIC_DRAW,
    );

    // Create a buffer for texture coordinates
    this.textureCoordBuffer = this.gl.createBuffer();
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.textureCoordBuffer);

    const textureCoordinates = [
      0.0,
      0.0, // bottom left
      1.0,
      0.0, // bottom right
      0.0,
      1.0, // top left
      1.0,
      1.0, // top right
    ];

    this.gl.bufferData(
      this.gl.ARRAY_BUFFER,
      new Float32Array(textureCoordinates),
      this.gl.STATIC_DRAW,
    );
  }

  // Initialize the color from CSS variable if available
  initializeAccentColor() {
    try {
      // Get the accent color from CSS variables
      const accentColor = `hsl(${getComputedStyle(document.documentElement)
        .getPropertyValue("--accent")
        .trim()})`;

      if (accentColor) {
        // If it's an HSL value, convert it to hex
        if (accentColor.startsWith("hsl")) {
          this.colorValue = this.hslToHex(accentColor);
        } else {
          // If it's already a hex or other format, use it directly
          this.colorValue = accentColor.startsWith("#")
            ? accentColor
            : `#${accentColor}`;
        }
        console.log("Using accent color for default:", this.colorValue);
      }
    } catch (error) {
      // If there's any error, fall back to the default color
      console.log("Falling back to default color:", this.colorValue);
    }
  }

  handleTaskCompleted(event) {
    // Extract coordinates and color from the event
    const { clientX, clientY, color } = event.detail;

    console.log("Task completed event received", {
      clientX,
      clientY,
      color,
      eventDetail: event.detail,
      defaultColor: this.colorValue,
    });

    // Start the animation with the task's color
    this.triggerEffect(clientX, clientY, color);
  }

  triggerEffect(x, y, color) {
    // Make sure we have a valid WebGL context and program
    if (!this.gl || !this.program || !this.programInfo) {
      return;
    }

    try {
      // Set animation parameters
      this.startTime = performance.now();
      this.mouseX = x;
      this.mouseY = y;

      // Use the provided color (from the task) if available, otherwise use the default
      this.currentEffectColor = color || this.colorValue;
      console.log("Using color for effect:", {
        providedColor: color,
        defaultColor: this.colorValue,
        finalColor: this.currentEffectColor,
      });

      // Update color uniform
      this.gl.uniform3f(
        this.programInfo.uniformLocations.color,
        ...this.hexToRgb(this.currentEffectColor),
      );

      // Start animation
      this.isAnimating = true;
      this.animate(this.startTime);
    } catch (error) {
      // Silently fail
      console.error("Error triggering effect:", error);
    }
  }

  animate(currentTime) {
    if (!this.isAnimating) return;

    if (!this.startTime) {
      this.startTime = currentTime;
    }

    // Calculate elapsed time as a percentage of total duration
    const elapsed = currentTime - this.startTime;
    const normalizedTime = Math.min(elapsed / this.durationValue, 1.0);

    // Clear the canvas
    this.gl.clear(this.gl.COLOR_BUFFER_BIT);

    // Only render if animation is still active
    if (normalizedTime < 1.0) {
      this.render(normalizedTime);
      this.animationFrameId = requestAnimationFrame((time) =>
        this.animate(time),
      );
    } else {
      // Animation complete, clean up
      this.animationFrameId = null;
      this.startTime = null;
      this.isAnimating = false;
    }
  }

  render(normalizedTime) {
    const gl = this.gl;

    // Make sure we have a valid program
    if (!this.program || !this.programInfo) {
      return;
    }

    // Use our shader program
    gl.useProgram(this.program);

    // Set up vertex position attribute
    gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);
    gl.vertexAttribPointer(
      this.programInfo.attribLocations.vertexPosition,
      2, // 2 components per vertex
      gl.FLOAT, // the data is 32bit floats
      false, // don't normalize
      0, // stride
      0, // offset
    );
    gl.enableVertexAttribArray(this.programInfo.attribLocations.vertexPosition);

    // Set up texture coordinate attribute
    gl.bindBuffer(gl.ARRAY_BUFFER, this.textureCoordBuffer);
    gl.vertexAttribPointer(
      this.programInfo.attribLocations.textureCoord,
      2, // 2 components per vertex
      gl.FLOAT, // the data is 32bit floats
      false, // don't normalize
      0, // stride
      0, // offset
    );
    gl.enableVertexAttribArray(this.programInfo.attribLocations.textureCoord);

    // Set uniforms
    gl.uniform2f(
      this.programInfo.uniformLocations.resolution,
      this.canvas.width,
      this.canvas.height,
    );

    gl.uniform2f(
      this.programInfo.uniformLocations.mousePosition,
      this.mouseX * window.devicePixelRatio,
      this.canvas.height - this.mouseY * window.devicePixelRatio, // Flip Y coordinate
    );

    gl.uniform1f(this.programInfo.uniformLocations.time, normalizedTime);

    // Make sure we're using the current effect color
    const renderColor = this.currentEffectColor || this.colorValue;

    // Only log on first frame to avoid console spam
    if (normalizedTime < 0.1) {
      console.log("Rendering with color:", {
        currentEffectColor: this.currentEffectColor,
        defaultColor: this.colorValue,
        finalRenderColor: renderColor,
      });
    }

    // Use the current effect color for rendering
    gl.uniform3f(
      this.programInfo.uniformLocations.color,
      ...this.hexToRgb(renderColor),
    );

    gl.uniform1f(
      this.programInfo.uniformLocations.intensity,
      this.intensityValue,
    );

    gl.uniform1f(
      this.programInfo.uniformLocations.pulseWidth,
      this.pulseWidthValue,
    );

    gl.uniform1f(
      this.programInfo.uniformLocations.pulseSpeed,
      this.pulseSpeedValue,
    );

    // Draw the square
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }

  // Convert hex color to RGB values (0-1 range for WebGL)
  hexToRgb(hex) {
    try {
      // Default to a fallback color if hex is invalid
      if (!hex || typeof hex !== "string") {
        console.warn("Invalid hex color, using fallback:", hex);
        hex = "#deaf13"; // Fallback color
      }

      // Remove # if present
      hex = hex.replace(/^#/, "");

      // Ensure we have a valid hex color (6 characters)
      if (!/^[0-9A-Fa-f]{6}$/.test(hex)) {
        console.warn("Invalid hex format, using fallback:", hex);
        hex = "deaf13"; // Fallback color without #
      }

      // Parse hex values
      const r = parseInt(hex.substring(0, 2), 16) / 255;
      const g = parseInt(hex.substring(2, 4), 16) / 255;
      const b = parseInt(hex.substring(4, 6), 16) / 255;

      // Check for NaN values
      if (isNaN(r) || isNaN(g) || isNaN(b)) {
        console.warn("Error parsing hex color, using fallback");
        return [0.87, 0.68, 0.07]; // Fallback color in RGB (0-1)
      }

      return [r, g, b];
    } catch (error) {
      console.error("Error in hexToRgb:", error);
      return [0.87, 0.68, 0.07]; // Fallback color in RGB (0-1)
    }
  }

  // Test method to manually trigger the effect
  testEffect() {
    // Set mouse position to center of screen if not set
    if (!this.mouseX || !this.mouseY) {
      this.mouseX = window.innerWidth / 2;
      this.mouseY = window.innerHeight / 2;
    }

    // Get the current color from the color picker if available
    const colorPicker = document.getElementById("effect-color");
    const testColor = colorPicker ? colorPicker.value : this.colorValue;

    // Store as current effect color
    this.currentEffectColor = testColor;

    // Update uniforms with current values
    this.gl.uniform3f(
      this.programInfo.uniformLocations.color,
      ...this.hexToRgb(this.currentEffectColor),
    );

    // Start the animation
    this.startTime = performance.now();
    this.isAnimating = true;
    this.animate(this.startTime);
  }

  // Setup keyboard shortcut for debug panel
  setupDebugPanelShortcut() {
    this.handleKeyDown = (event) => {
      // Check for Option + Shift + D (Mac) - this is the "Î" key with those modifiers
      if (event.altKey && event.shiftKey && event.key === "Î") {
        event.preventDefault();
        this.toggleDebugPanel();
      }
    };

    document.addEventListener("keydown", this.handleKeyDown);
  }

  // Toggle debug panel visibility
  toggleDebugPanel() {
    let debugPanel = document.getElementById("effect-debug-panel");

    if (debugPanel) {
      // If panel exists, toggle visibility
      if (debugPanel.style.display === "none") {
        debugPanel.style.display = "block";
      } else {
        debugPanel.style.display = "none";
      }
    } else {
      // Create the debug panel if it doesn't exist
      this.addDebugInfo();
    }
  }

  // Add debug information to the page
  addDebugInfo() {
    // Create the debug panel
    const debugDiv = document.createElement("div");
    debugDiv.id = "effect-debug-panel";
    debugDiv.style.position = "fixed";
    debugDiv.style.bottom = "50px";
    debugDiv.style.right = "10px";
    debugDiv.style.backgroundColor = "rgba(0, 0, 0, 0.7)";
    debugDiv.style.color = "white";
    debugDiv.style.padding = "10px";
    debugDiv.style.borderRadius = "5px";
    debugDiv.style.zIndex = "10000";
    debugDiv.style.fontFamily = "monospace";
    debugDiv.style.fontSize = "12px";
    debugDiv.style.maxWidth = "300px";
    debugDiv.style.display = "block"; // Ensure it's visible

    const webglInfo = this.gl ? "WebGL Supported" : "WebGL Not Supported";
    const programInfo = this.program
      ? "Shader Program Linked"
      : "Shader Program Not Linked";

    debugDiv.innerHTML = `
      <div style="font-weight: bold; margin-bottom: 5px;">Task Completion Effect Debug</div>
      <div>${webglInfo}</div>
      <div>${programInfo}</div>

      <div style="margin-top: 10px;">
        <label style="display: block; margin-bottom: 5px;">
          Color:
          <input type="color" id="effect-color" value="${this.colorValue}" style="margin-left: 5px;">
        </label>

        <label style="display: block; margin-bottom: 5px;">
          Duration (ms):
          <input type="range" id="effect-duration" min="500" max="5000" step="100" value="${this.durationValue}" style="width: 100%;">
          <span id="duration-value">${this.durationValue}</span>
        </label>

        <label style="display: block; margin-bottom: 5px;">
          Opacity:
          <input type="range" id="effect-intensity" min="0.1" max="1.0" step="0.05" value="${this.intensityValue}" style="width: 100%;">
          <span id="intensity-value">${this.intensityValue}</span>
        </label>

        <label style="display: block; margin-bottom: 5px;">
          Edge Width:
          <input type="range" id="effect-pulse-width" min="0.01" max="0.3" step="0.01" value="${this.pulseWidthValue}" style="width: 100%;">
          <span id="pulse-width-value">${this.pulseWidthValue}</span>
        </label>

        <label style="display: block; margin-bottom: 5px;">
          Speed:
          <input type="range" id="effect-pulse-speed" min="0.5" max="3.0" step="0.1" value="${this.pulseSpeedValue}" style="width: 100%;">
          <span id="pulse-speed-value">${this.pulseSpeedValue}</span>
        </label>
      </div>

      <button id="test-effect-btn" style="margin-top: 10px; padding: 5px; width: 100%;">Test Effect</button>
    `;

    document.body.appendChild(debugDiv);

    // Add event listeners for controls
    document.getElementById("effect-color").addEventListener("change", (e) => {
      this.colorValue = e.target.value;
    });

    document
      .getElementById("effect-duration")
      .addEventListener("input", (e) => {
        this.durationValue = parseInt(e.target.value);
        document.getElementById("duration-value").textContent =
          this.durationValue;
      });

    document
      .getElementById("effect-intensity")
      .addEventListener("input", (e) => {
        this.intensityValue = parseFloat(e.target.value);
        document.getElementById("intensity-value").textContent =
          this.intensityValue;
      });

    document
      .getElementById("effect-pulse-width")
      .addEventListener("input", (e) => {
        this.pulseWidthValue = parseFloat(e.target.value);
        document.getElementById("pulse-width-value").textContent =
          this.pulseWidthValue;
      });

    document
      .getElementById("effect-pulse-speed")
      .addEventListener("input", (e) => {
        this.pulseSpeedValue = parseFloat(e.target.value);
        document.getElementById("pulse-speed-value").textContent =
          this.pulseSpeedValue;
      });

    // Add click handler for the test button
    document.getElementById("test-effect-btn").addEventListener("click", () => {
      this.testEffect();
    });
  }
}
